/**
 * Store hours, upgraded in the browser.
 *
 * The block server-renders a dateless fallback -- weekday name and hours, no
 * dates, no today marker. That is deliberate. Storefront pages are held in
 * Shopify's CDN cache, so anything Liquid derives from `now` freezes at render
 * time. "Monday 9:00 AM - 5:00 PM" is as true next week as today; "Monday,
 * Aug 3" is wrong within a day. The fallback therefore carries only what does
 * not go stale, and this script adds the rest at page load.
 *
 * Everything time-dependent is resolved against the shop's own zone, not the
 * visitor's. A shopper in Vancouver asking "are they open?" means open in
 * Ottawa.
 */

const DAY_MS = 86400000;

/** Today in `zone`, as YYYY-MM-DD. Read via en-CA, which formats that way. */
export function todayIn(zone, now = new Date()) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: zone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
}

/** Minutes since midnight in `zone`. Used only to decide open/closed now. */
export function minutesNowIn(zone, now = new Date()) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: zone,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(now);
  const get = (t) => Number(parts.find((p) => p.type === t)?.value ?? 0);
  return get("hour") * 60 + get("minute");
}

/** "09:00" -> 540. Returns null for anything unparseable rather than guessing. */
export function toMinutes(hhmm) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm ?? "").trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}

/** 540 -> "9:00 AM". */
export function formatTime(minutes) {
  const h24 = Math.floor(minutes / 60);
  const min = String(minutes % 60).padStart(2, "0");
  const period = h24 >= 12 ? "PM" : "AM";
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${min} ${period}`;
}

/** "2026-08-03" -> "Aug 3". No year: every row shown is inside one week. */
export function formatDate(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${months[m - 1]} ${d}`;
}

/**
 * The rows to show: `days` entries starting from today.
 *
 * The app writes a longer horizon than is displayed, which is what makes this
 * safe -- a page served from cache still finds today in the array and slices
 * forward from there. Entries before today are dropped, not rendered.
 */
export function selectDays(entries, today, days = 7) {
  return entries
    .filter((e) => typeof e?.date === "string" && e.date >= today)
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(0, days);
}

/**
 * Open now, and until when. Reported only for today's entry: "open" is a
 * statement about right now, and no other row can answer it.
 */
export function openState(entries, today, nowMinutes) {
  const entry = entries.find((e) => e.date === today);
  if (!entry || entry.closed) return { open: false, entry: entry ?? null };

  const open = toMinutes(entry.open);
  const close = toMinutes(entry.close);
  if (open === null || close === null || close <= open) {
    return { open: false, entry, unparseable: true };
  }
  if (nowMinutes >= open && nowMinutes < close) {
    return { open: true, until: formatTime(close) };
  }
  if (nowMinutes < open) {
    return { open: false, opensToday: formatTime(open) };
  }
  return { open: false };
}

/** The next entry that is actually open, for the "opens ..." half of the badge. */
export function nextOpening(entries, today, nowMinutes) {
  for (const e of entries) {
    if (e.date < today || e.closed) continue;
    const open = toMinutes(e.open);
    if (open === null) continue;
    if (e.date === today && nowMinutes >= open) continue;
    return { entry: e, at: formatTime(open) };
  }
  return null;
}

function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/**
 * Replace the fallback table with the dated one.
 *
 * A parse failure prints the raw value rather than substituting "Closed".
 * Telling a shopper the shop is shut when it is open is the one output worth
 * designing against -- this is a pickup-only store, so that is a wasted trip.
 */
export function render(root, config, now = new Date()) {
  const zone = config.timezone || "America/Toronto";
  const entries = Array.isArray(config.entries) ? config.entries : [];
  if (entries.length === 0) return; // leave the fallback alone

  const today = todayIn(zone, now);
  const rows = selectDays(entries, today, config.days || 7);
  if (rows.length === 0) return;

  const list = el("ul", "store-hours__list");
  for (const row of rows) {
    const item = el("li", "store-hours__row");
    if (row.date === today) item.classList.add("is-today");
    if (row.closed) item.classList.add("is-closed");

    const day = el("span", "store-hours__day");
    day.append(el("span", "store-hours__day-name", row.day));
    day.append(el("span", "store-hours__date", formatDate(row.date)));
    item.append(day);

    let text;
    if (row.closed) {
      text = config.closedLabel || "Closed";
    } else {
      const open = toMinutes(row.open);
      const close = toMinutes(row.close);
      text = open === null || close === null
        ? `${row.open} - ${row.close}`   // unparseable: show it verbatim
        : `${formatTime(open)} – ${formatTime(close)}`;
    }
    item.append(el("span", "store-hours__time", text));
    list.append(item);
  }

  root.replaceChildren(list);

  if (config.showStatus) {
    const state = openState(entries, today, minutesNowIn(zone, now));
    let label;
    if (state.open) {
      // Open is not by itself an invitation: visits are arranged first, so the
      // hint carries what to do with the fact.
      const hint = String(config.statusHint ?? "").trim();
      label = hint
        ? `Open now until ${state.until} — ${hint}`
        : `Open now — until ${state.until}`;
    } else {
      const next = nextOpening(entries, today, minutesNowIn(zone, now));
      label = next
        ? `Closed — opens ${next.entry.date === today ? "today" : next.entry.day} at ${next.at}`
        : "Closed";
    }

    // The block places this next to the button that starts the conversation,
    // where when-we-are-open and how-to-reach-us belong together. Falling back
    // to the top of the hours card keeps any block that does not.
    const className = `store-hours__status ${state.open ? "is-open" : "is-shut"}`;
    const slot = root.closest("[data-store-hours]")?.querySelector("[data-store-hours-status]");
    if (slot) {
      slot.className = className;
      slot.textContent = label;
      slot.removeAttribute("hidden");
    } else {
      root.prepend(el("p", className, label));
    }
  }
}

/**
 * Turn the entrance picture's link into the dialog beside it.
 *
 * The link is not decorative and does not depend on this running: its href is
 * the full-size photograph, so with no JavaScript, or on a browser with no
 * <dialog>, clicking it still gets the visitor a bigger picture. This upgrades
 * that to an overlay when there is something better to offer, and bails
 * silently when there is not -- the same rule the chat button follows, that
 * nothing on this block is offered until it works.
 *
 * Escape, the focus trap and returning focus to the link afterwards are all
 * `showModal()`'s doing. The close button is a `method="dialog"` submit and
 * needs nothing from here either.
 */
function initEntranceZoom() {
  for (const trigger of document.querySelectorAll("[data-entrance-zoom]")) {
    const dialog = document.getElementById(trigger.dataset.entranceZoom);
    if (!dialog || typeof dialog.showModal !== "function") continue;

    // The dialog's picture carries its URLs in data- attributes and has no src
    // at all until this runs, so a 2200px photograph nobody opens costs a
    // visitor nothing. `loading="lazy"` cannot do that job: lazy defers an
    // image below the viewport, and an image in a closed dialog is in a
    // display:none subtree with no box to be below anything, so the browser
    // fetched it on every page load -- 450 KB, measured, before this.
    //
    // Fetching on the way to the click -- a hover, or a tab onto the link --
    // puts the wait before the click rather than after it, and the click path
    // calls this too for anyone who gets there without either. srcset first:
    // set src on its own and the browser may commit to it before the candidate
    // list exists.
    const warm = () => {
      const full = dialog.querySelector("img[data-src]");
      if (!full) return;
      if (full.dataset.srcset) full.srcset = full.dataset.srcset;
      full.src = full.dataset.src;
      delete full.dataset.src;
      delete full.dataset.srcset;
    };
    trigger.addEventListener("pointerenter", warm, { once: true });
    trigger.addEventListener("focus", warm, { once: true });

    trigger.addEventListener("click", (event) => {
      // A modified click is a request for the file itself -- new tab, new
      // window, save. Let the href do what it says it does.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      warm();
      dialog.showModal();

      // On a portrait phone the picture is wider than its scroller, so it opens
      // showing whichever end the browser happens to start at. The door is in
      // the middle. Only ever a no-op when the picture fits.
      const scroller = dialog.querySelector("[data-entrance-scroll]");
      if (scroller) {
        const centre = () => {
          scroller.scrollLeft = (scroller.scrollWidth - scroller.clientWidth) / 2;
        };
        centre();
        // Again once the picture has its real width, since a lazy image that
        // has not arrived yet gives a scrollWidth with nothing to scroll.
        const full = scroller.querySelector("img");
        if (full && !full.complete) full.addEventListener("load", centre, { once: true });
      }
    });

    // The dialog fills the viewport and paints nothing; the panel inside it is
    // what is visible. So a click that lands on the dialog itself rather than
    // on a descendant is a click on the empty space around the picture.
    dialog.addEventListener("click", (event) => {
      if (event.target === dialog) dialog.close();
    });
  }
}

/** Read the JSON each block embedded, and upgrade every instance on the page. */
function init() {
  initEntranceZoom();

  for (const root of document.querySelectorAll("[data-store-hours-target]")) {
    const host = root.closest("[data-store-hours]");
    const script = host?.querySelector("[data-store-hours-config]");
    if (!script) continue;
    try {
      render(root, JSON.parse(script.textContent));
    } catch (error) {
      // Leave the server-rendered fallback in place. Hours the shopper can
      // read beat an empty card.
      console.error("store-hours: could not upgrade", error);
    }
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
